import React, {useCallback, useEffect, useState} from "react";
import {Button, Message, Popover, toaster, Whisper} from "rsuite";
import {PressureCalibrationHistoryDto} from "../../../../../../api";
import MechanismApi from "../../../../../../services/Mechanism.api";
import "./AltitudeCalibrationInfo.css";

interface AltitudeCalibrationInfoProps {
  mechanismId: string;
  // Меняется после сохранения ручной высоты или расчёта — перечитать историю
  refreshKey: number;
  disabled?: boolean;
  onReset?: () => void;
}

// Числа по-русски: 4,2; со знаком: +4,2 / −4,2
const formatNumber = (value: number | null | undefined, digits = 1, signed = false): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  const number = Number(value);
  const text = Math.abs(number).toFixed(digits).replace(".", ",");
  if (signed && Math.abs(number) < Math.pow(10, -digits) / 2) return text;
  if (number < 0) return `−${text}`;
  return signed ? `+${text}` : text;
};

const formatDate = (value: string | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

const isTouchDevice = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: none)").matches;

/**
 * Значок «i» рядом с ручной высотой: история калибровок датчика давления и сброс.
 */
const AltitudeCalibrationInfo: React.FC<AltitudeCalibrationInfoProps> = ({mechanismId, refreshKey, disabled, onReset}) => {
  const [history, setHistory] = useState<PressureCalibrationHistoryDto | null>(null);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!mechanismId) return;
    try {
      setHistory(await MechanismApi.getAltitudeCalibrations(mechanismId));
    } catch (e) {
      console.error("Ошибка при загрузке истории калибровок:", e);
    }
  }, [mechanismId]);

  useEffect(() => {
    load().then();
  }, [load, refreshKey]);

  const onResetClick = async () => {
    if (!window.confirm("Удалить все сохранения ручной высоты и сбросить поправку датчика давления?")) return;
    setResetting(true);
    try {
      setHistory(await MechanismApi.resetAltitudeCalibrations(mechanismId));
      toaster.push(<Message type="success" closable>Калибровка сброшена</Message>, {placement: "topEnd"});
      onReset?.();
    } catch (e) {
      console.error("Ошибка при сбросе калибровки:", e);
      toaster.push(<Message type="error" closable>Не удалось сбросить калибровку</Message>, {placement: "topEnd"});
    } finally {
      setResetting(false);
    }
  };

  const count = history?.count ?? 0;
  const usedCount = history?.usedCount ?? 0;
  const items = history?.items ?? [];

  const speaker = (
    <Popover title="Калибровка по ручной высоте" className="altitude-calibration-info__popover">
      {count === 0 ? (
        <p className="altitude-calibration-info__line">
          Сохранений пока нет. Сохраните ручную высоту — по ней откалибруется датчик давления.
        </p>
      ) : (
        <div className="altitude-calibration-info__body">
          <p className="altitude-calibration-info__line">
            Сохранений: <b>{count}</b>
            {count > usedCount && <> (в поправке последние {usedCount})</>}
          </p>
          <p className="altitude-calibration-info__line">
            Поправка датчика: <b>{formatNumber(history?.averageOffset, 2, true)} мм рт. ст.</b>
            {" "}(≈ {formatNumber(history?.averageDeviation, 1, true)} м)
          </p>
          {history?.spread !== null && history?.spread !== undefined && (
            <p className="altitude-calibration-info__line altitude-calibration-info__line--spaced">
              Разброс: ±{formatNumber(history.spread)} м
            </p>
          )}
          <div className="altitude-calibration-info__table-wrap">
            <table className="altitude-calibration-info__table">
              <thead>
              <tr>
                <th>Дата</th>
                <th className="altitude-calibration-info__number">Высота</th>
                <th className="altitude-calibration-info__number">Отклонение</th>
              </tr>
              </thead>
              <tbody>
              {items.map(item => (
                <tr
                  key={item.id}
                  className={item.used ? undefined : "altitude-calibration-info__row--unused"}
                  title={item.source ?? ""}
                >
                  <td>{formatDate(item.calibratedAt)}</td>
                  <td className="altitude-calibration-info__number">{formatNumber(item.altitude)} м</td>
                  <td className="altitude-calibration-info__number">{formatNumber(item.deviation, 1, true)} м</td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          <p className="altitude-calibration-info__hint">
            Отклонение — насколько авт. высота без поправки отличалась от ручной.
            Поправка — среднее по последним {history?.maxUsed ?? 10} сохранениям.
          </p>
        </div>
      )}
      {count > 0 && (
        <Button
          size="sm"
          color="red"
          appearance="ghost"
          block
          loading={resetting}
          disabled={disabled || resetting}
          onClick={onResetClick}
        >
          Сбросить все показания
        </Button>
      )}
    </Popover>
  );

  return (
    <Whisper trigger={isTouchDevice() ? "click" : "hover"} enterable placement="autoVertical" speaker={speaker}>
      <span
        role="button"
        aria-label={`Калибровка по ручной высоте: сохранений ${count}`}
        className="altitude-calibration-info__icon"
      >
        i
      </span>
    </Whisper>
  );
};

export default AltitudeCalibrationInfo;
